<template>
    <div class="group relative inline-flex">
        <UButton
            v-if="src"
            @click="removeAvatar()"
            icon="i-ph-x-bold"
            size="2xs"
            color="white"
            variant="solid"
            class="hidden group-hover:flex absolute -top-2 -right-2 z-10 border-2"
            :ui="{ rounded: 'rounded-full' }"
        />

        <label class="m-px relative inline-flex items-center justify-center rounded-md shadow-sm bg-transparent overflow-hidden text-gray-900 ring-1 ring-gray-300 cursor-pointer">
            <UAvatar
                size="3xl"
                :alt="placeholder"
                :src="src"
                :ui="{ rounded: 'rounded-none' }"
            />

            <div v-if="uploading" class="absolute inset-0 flex items-center justify-center bg-black/40">
                <UIcon name="i-ph-spinner" class="text-white size-6 animate-spin" />
            </div>

            <input
                type="file"
                accept="image/*"
                class="absolute inset-0 invisible"
                @change="uploadAvatar"
                :disabled="uploading"
            />
        </label>
    </div>
</template>

<script setup lang="ts">
const emit = defineEmits<{
    'update:modelValue': [string]
}>()

const props = withDefaults(defineProps<{
    modelValue?: string
    placeholder?: string
    bucket?: string
    folder?: string
    uploadUrl?: string
}>(), {
    bucket: 'avatars',
    folder: '',
})

const supabase = useSupabaseClient()
const toast = useToast()
const config = useRuntimeConfig()

const uploading = ref(false)
const src = ref('')
const localFileSet = ref(false)

watch(() => props.modelValue, (val) => {
    if (localFileSet.value) return
    if (val) {
        downloadImage()
    } else {
        src.value = ''
    }
}, { immediate: true })

async function downloadImage() {
    if (!props.modelValue) return
    try {
        const objectPath = props.folder ? `${props.folder}/${props.modelValue}` : props.modelValue
        // Server-uploaded buckets are public-read with no storage policies, so display them by public URL.
        if (props.uploadUrl) {
            src.value = `${config.public.supabase.url}/storage/v1/object/public/${props.bucket}/${objectPath}`
            return
        }
        const { data, error } = await supabase.storage.from(props.bucket).download(objectPath)
        if (error) throw error
        src.value = URL.createObjectURL(data)
    } catch (error: any) {
        console.error('Error downloading image:', error.message)
        toast.add({ title: 'Could not load image', description: error.message, color: 'red' })
    }
}

async function uploadAvatar(event: any) {
    const files = event.target?.files
    if (!files || files.length === 0) return

    const file = files[0]
    const previousSrc = src.value
    const previousLocalFlag = localFileSet.value

    src.value = URL.createObjectURL(file)
    localFileSet.value = true
    uploading.value = true

    try {
        if (props.uploadUrl) {
            const body = new FormData()
            body.append('file', file)
            const { fileName } = await $fetch<{ fileName: string }>(props.uploadUrl, { method: 'POST', body })
            emit('update:modelValue', fileName)
            return
        }

        const fileExt = file.name.split('.').pop()
        const fileName = `${Math.random()}.${fileExt}`
        const objectPath = props.folder ? `${props.folder}/${fileName}` : fileName

        const { error: uploadError } = await supabase.storage
            .from(props.bucket)
            .upload(objectPath, file)

        if (uploadError) throw uploadError

        emit('update:modelValue', fileName)
    } catch (error: any) {
        const message = error?.data?.message || error.message
        console.error('Upload failed:', message)
        toast.add({ title: 'Upload failed', description: message, color: 'red' })
        src.value = previousSrc
        localFileSet.value = previousLocalFlag
    } finally {
        uploading.value = false
    }
}

function removeAvatar() {
    src.value = ''
    localFileSet.value = false
    emit('update:modelValue', '')
}
</script>
